// Component: TickFlash.jsx
// Purpose: Ambient tick-flash wrapper. When `value` CHANGES, the wrapper's
//   background flashes a teal (profit) or rose (loss) wash at 12% alpha and
//   decays to transparent over 240ms — the "a price just moved" heartbeat that
//   keeps a restrained monochrome feed feeling live. No flash on first mount.
//
//   Direction: sign={1|-1|0} forces teal / rose / neutral; sign="auto" compares
//   the new numeric value to the old. Rapid successive ticks retrigger cleanly
//   — each tick remounts a keyed wash overlay, so there's no stuck wash and no
//   animation pileup. Cheap: one absolutely-positioned overlay behind the
//   content (isolate + z-index:-1), no wrapping of children, memoised.
//
//   Reduced-motion → no flash at all (framer useReducedMotion).
//
// Usage:
//   <TickFlash value={ltp} sign="auto"><PriceBlock/></TickFlash>
//   <TickFlash value={pnl} sign={pnl >= 0 ? 1 : -1}>{children}</TickFlash>

import React, { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { cx } from "./cx";

// Signed wash tints — slash-syntax alpha over the signed-color RGB channels.
// neutral uses --bg-3 so a value that changed but didn't move (sign 0 / equal)
// still registers a subtle blink.
const WASH = {
  pos: "rgb(var(--color-profit-rgb) / 0.12)",
  neg: "rgb(var(--color-loss-rgb) / 0.12)",
  neutral: "var(--bg-3)",
};

// Decay curve — --ease-out (0,0,0.2,1) over --dur-slow (240ms).
const DECAY = { duration: 0.24, ease: [0, 0, 0.2, 1] };

function resolveDir(sign, prev, next) {
  if (sign === "auto") {
    const a = Number(prev);
    const b = Number(next);
    if (Number.isFinite(a) && Number.isFinite(b)) {
      return b > a ? "pos" : b < a ? "neg" : "neutral";
    }
    return "neutral";
  }
  const s = Number(sign);
  return s > 0 ? "pos" : s < 0 ? "neg" : "neutral";
}

function TickFlashBase({ value, sign = "auto", children, className, ...props }) {
  const reduced = useReducedMotion();
  const prev = useRef(value);
  const first = useRef(true);
  // { n, dir } — bumping `n` remounts the wash so the decay replays cleanly.
  const [tick, setTick] = useState({ n: 0, dir: null });

  useEffect(() => {
    if (first.current) {
      first.current = false;
      prev.current = value;
      return; // no flash on initial mount
    }
    if (value === prev.current) return; // unchanged → nothing to do
    const dir = resolveDir(sign, prev.current, value);
    prev.current = value;
    if (reduced) return; // reduced-motion → no flash
    setTick((t) => ({ n: t.n + 1, dir }));
  }, [value, sign, reduced]);

  return (
    <div className={cx("ds-tickflash", className)} {...props}>
      {tick.dir && !reduced && (
        <motion.span
          key={tick.n}
          className="ds-tickflash-wash"
          aria-hidden="true"
          style={{ background: WASH[tick.dir] }}
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={DECAY}
        />
      )}
      {children}
    </div>
  );
}

// Memoised: the wrapper only needs to re-run when value/sign change. children
// identity usually changes with the parent anyway, but memo keeps a parent
// re-render that leaves this node's props untouched from re-flashing.
export const TickFlash = React.memo(TickFlashBase);

export default TickFlash;
